import { useEffect, useState } from "react";

/**
 * The table pickers' server search (6,000-table scale test): one page of a large source is not
 * every table, so Run now's and Retry tables' search asks the server across all of them. The
 * typed value settles first, so the server is asked once a word is typed, not on every key.
 */

/** How long the table search settles before the server is asked. */
export const TABLE_SEARCH_DEBOUNCE_MS = 250;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

/**
 * Whether the server's part of the list is for an older search than the one typed: the search is
 * settling, or its answer is still the previous one held in place (`isPlaceholderData`). Never on
 * the first read or a background refetch of the same search.
 */
export function searchPending(
  search: { settling: boolean },
  query: { isFetching: boolean; isPlaceholderData: boolean },
): boolean {
  return search.settling || (query.isFetching && query.isPlaceholderData);
}

export function useServerTableSearch() {
  const [search, setSearch] = useState("");
  const settled = useDebouncedValue(search, TABLE_SEARCH_DEBOUNCE_MS).trim();
  return {
    /** What the server is asked for. */
    settled,
    setSearch,
    /** Typed but not yet asked: the grid shows its loading state meanwhile. */
    settling: search.trim() !== settled,
  };
}

/** Said under a picker whose list is cut at one page. */
export function cutListNote(page: number, total: number, searching: boolean): string | null {
  if (total <= page) return null;
  const of = total.toLocaleString("en-US");
  return searching
    ? `Showing ${page} of ${of} matches. Type more to narrow the list.`
    : `Showing ${page} of ${of}. Search to find a table.`;
}

/** Said when the table list could not be read. */
export const TABLE_LIST_ERROR = "The tables could not be loaded. Close this and try again.";
